// Dashboard's "Club activity" card — matched to the literal tile layout in
// Claude Design "Persona Dashboard Redesign" / DashboardRevampContent.dc.html
// (`clubCards`): icon + TAG + when on one row, then title, sub, then a
// face-stack + label + CTA pill row. Three tiles, like the design.
//
// The design's sample tiles (Weekend Arena / Puzzle rush / Monthly check-in)
// are mock content. Everything here is real Clubs API data instead, drawn from
// whatever the club actually has, in priority order: active event → open
// challenges → open polls → announcement → club leaderboard. A quiet club
// still fills the card from the last two rather than the card vanishing (which
// left Review game stretched across the full row). No club at all → a single
// join CTA. Nothing is invented: there is no assessment/"monthly check-in"
// concept in this API, and face stacks are only drawn where the API exposes
// real names.

import React from 'react';
import {StyleSheet, View, Pressable} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {Image} from 'react-native';
import CCText from '@/Atoms/CCText';
import AnimatedBar from '@/Atoms/AnimatedBar';
import CCColors from '@/Utils/CCColors';
import {hoverLift, pressSink, transition} from '@/Utils/motion';
import {ScreenName} from '@/navigators/StackConstants';
import {initialsForName} from '@/Screens/Leaderboard/useLeaderboardData';
import {dsIcons} from '../../designIcons';
import {
  CLUB_SUBTITLE_BY_PERSONA,
  Persona,
} from '../../personaHelpers';
import {
  useMyClubs,
  useClubActiveEvent,
  useClubChallenges,
  useClubPolls,
  useClubLeaderboard,
  useClubDirectory,
} from '@/Hooks/ClubsApi/useClubsQueries';
import {radii} from '@/Utils/radius';

const MAX_TILES = 3;
const FACE_PALETTE = ['#1ECF52', '#67B3E1', '#E17846', '#937ADB', '#DB7ACC'];

// Compact right-aligned timestamp, matching the design's "Sat 6:00 PM" /
// "Today" / "Monday" treatment.
function shortWhen(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  const withinWeek =
    Math.abs(d.getTime() - now.getTime()) < 7 * 24 * 60 * 60 * 1000;
  return withinWeek
    ? d.toLocaleDateString(undefined, {weekday: 'short'})
    : d.toLocaleDateString(undefined, {day: 'numeric', month: 'short'});
}

function FaceStack({names}: {names: string[]}) {
  if (!names.length) return null;
  return (
    <View style={styles.faceStack}>
      {names.slice(0, 3).map((n, i) => (
        <View
          key={`${n}-${i}`}
          style={[
            styles.face,
            {backgroundColor: FACE_PALETTE[i % FACE_PALETTE.length]},
            i === 0 && {marginLeft: 0},
          ]}>
          <CCText fontSize={8} fontWeight="bold" color={CCColors.White}>
            {initialsForName(n)}
          </CCText>
        </View>
      ))}
    </View>
  );
}

interface Tile {
  key: string;
  icon: any;
  tag: string;
  tagColor: string;
  when?: string;
  title: string;
  sub: string;
  faces?: string[];
  faceLabel: string;
  cta: string;
  onPress: () => void;
  progressPct?: number;
}

function ClubTile({tile}: {tile: Tile}) {
  return (
    <Pressable
      onPress={tile.onPress}
      style={({hovered, pressed}: any) => [
        styles.tile,
        {cursor: 'pointer'},
        hovered && styles.tileHovered,
        pressed && styles.tilePressed,
      ]}>
      <View style={styles.tileTopRow}>
        <Image source={tile.icon} style={styles.tileIcon} resizeMode="contain" />
        <CCText
          fontSize={9.5}
          fontWeight="bold"
          color={tile.tagColor}
          style={styles.tag}>
          {tile.tag}
        </CCText>
        {!!tile.when && (
          <CCText fontSize={11} color={CCColors.TextColor.Grey}>
            {tile.when}
          </CCText>
        )}
      </View>

      <CCText
        fontSize={13.5}
        fontWeight="bold"
        color={CCColors.TextColor.Dark}
        style={{marginTop: 9}}
        showEllipses>
        {tile.title}
      </CCText>
      <CCText
        fontSize={11.5}
        color={CCColors.TextColor.Grey}
        style={{marginTop: 1}}
        showEllipses>
        {tile.sub}
      </CCText>

      {tile.progressPct != null && (
        <AnimatedBar
          percent={tile.progressPct}
          color={CCColors.Green}
          height={4}
          trackColor={CCColors.Primary.lightBrown}
          style={{marginTop: 9}}
        />
      )}

      <View style={styles.tileBottomRow}>
        <FaceStack names={tile.faces || []} />
        <CCText
          fontSize={11}
          color={CCColors.TextColor.Grey}
          style={{flex: 1}}
          showEllipses>
          {tile.faceLabel}
        </CCText>
        <View style={styles.ctaPill}>
          <CCText
            fontSize={11.5}
            fontWeight="bold"
            color={CCColors.Primary.Brown}>
            {tile.cta}
          </CCText>
        </View>
      </View>
    </Pressable>
  );
}

function ClubActivityCard({
  persona = 'self_learner',
  // True when the Review/Club row has collapsed to a column at narrow widths.
  // `flex: 1` would then divide vertical space instead of horizontal.
  stacked = false,
}: {
  persona?: Persona;
  stacked?: boolean;
}) {
  const navigation = useNavigation<any>();
  const {data: myClubsData, isLoading: isClubsLoading} = useMyClubs();
  const club = myClubsData?.clubs?.[0];
  const clubId = club?.id as number;

  const {data: eventData} = useClubActiveEvent(clubId);
  const {data: challengesData} = useClubChallenges(clubId);
  const {data: pollsData} = useClubPolls(clubId);
  const {data: clubLbData} = useClubLeaderboard(clubId, 'weekly');
  // Only needed for the not-in-a-club state, where real suggestions beat an
  // empty card. `enabled` is driven by the hook's own search arg, so this is a
  // cheap directory read shared with the Clubs screen's cache.
  const {data: directoryData} = useClubDirectory('');

  const activeEvent = eventData?.event;
  const openChallenges = (challengesData?.challenges || []).filter(
    c => c.state === 'open',
  );
  const openPolls = (pollsData?.polls || []).filter(p => !p.closed);
  const clubRows = clubLbData?.rows || [];

  const goToClub = () => {
    if (clubId) navigation.navigate(ScreenName.ClubRoom, {clubId});
  };
  const goToClubs = () => navigation.navigate(ScreenName.Clubs);

  if (isClubsLoading) return null;

  const tiles: Tile[] = [];

  if (club) {
    if (activeEvent) {
      tiles.push({
        key: 'event',
        icon: dsIcons.tournament,
        tag: 'CLUB EVENT',
        tagColor: '#E17846',
        when: shortWhen(activeEvent.ends_at),
        title: activeEvent.title,
        sub: `${activeEvent.progress} / ${activeEvent.goal} · you contributed ${activeEvent.my_contribution}`,
        progressPct: activeEvent.pct,
        faceLabel: `${Math.round(activeEvent.pct)}% complete`,
        cta: 'Join',
        onPress: goToClub,
      });
    }

    if (openChallenges.length > 0) {
      tiles.push({
        key: 'challenges',
        icon: dsIcons.positionsTrainer,
        tag: 'CHALLENGE',
        tagColor: '#67B3E1',
        when: shortWhen(openChallenges[0].created_at),
        title: `${openChallenges.length} open challenge${
          openChallenges.length === 1 ? '' : 's'
        }`,
        sub: `${openChallenges[0].time_control} · ${
          openChallenges[0].rated ? 'rated' : 'casual'
        }`,
        faces: openChallenges.map(c => c.created_by || '').filter(Boolean),
        faceLabel: `${club.member_count} members`,
        cta: 'Try',
        onPress: goToClub,
      });
    }

    if (openPolls.length > 0) {
      tiles.push({
        key: 'poll',
        icon: dsIcons.assessment,
        tag: 'POLL',
        tagColor: '#937ADB',
        when: shortWhen(openPolls[0].closes_at),
        title: openPolls[0].question,
        sub:
          openPolls[0].my_vote_option_id != null
            ? 'You voted'
            : 'Your vote is pending',
        faceLabel: `${openPolls[0].total_votes} vote${
          openPolls[0].total_votes === 1 ? '' : 's'
        }`,
        cta: openPolls[0].my_vote_option_id != null ? 'View' : 'Vote',
        onPress: goToClub,
      });
    }

    if (club.announcement?.text) {
      tiles.push({
        key: 'announcement',
        icon: dsIcons.chessEvents,
        tag: 'ANNOUNCEMENT',
        tagColor: '#DB7ACC',
        when: shortWhen(club.announcement.at),
        title: club.announcement.text,
        sub: `From ${club.name}`,
        faceLabel: `${club.member_count} members`,
        cta: 'Open',
        onPress: goToClub,
      });
    }

    if (clubRows.length > 0) {
      const leader = clubRows[0];
      tiles.push({
        key: 'club_leaderboard',
        icon: dsIcons.tournament,
        tag: 'CLUB LEADERBOARD',
        tagColor: '#3DAB9E',
        title: `${leader.name || leader.username} leads with ${
          leader.points
        } pts`,
        sub: 'Club standings this week',
        faces: clubRows.map(r => r.name || r.username).filter(Boolean),
        faceLabel: `${clubRows.length} ranked · ${club.member_count} members`,
        cta: 'View',
        onPress: goToClub,
      });
    }

    // Member of a club that genuinely has nothing going on yet.
    if (tiles.length === 0) {
      tiles.push({
        key: 'quiet_club',
        icon: dsIcons.gamePlay,
        tag: 'YOUR CLUB',
        tagColor: '#E17846',
        title: club.name,
        sub: 'No events, challenges or polls running right now',
        faceLabel: `${club.member_count} members`,
        cta: 'Open',
        onPress: goToClub,
      });
    }
  } else {
    // Not in a club — fill the card with real clubs you could actually join
    // (directory data), rather than a single empty-state line.
    const suggestions = (directoryData?.clubs || []).slice(0, MAX_TILES);
    suggestions.forEach(c => {
      tiles.push({
        key: `suggest_${c.id}`,
        icon: c.official ? dsIcons.tournament : dsIcons.chessEvents,
        tag: c.official ? 'OFFICIAL CLUB' : 'SUGGESTED CLUB',
        tagColor: c.official ? '#3DAB9E' : '#67B3E1',
        title: c.name,
        sub:
          c.description?.trim() ||
          (c.min_rating ? `Rating ${c.min_rating}+ to join` : 'Open to all'),
        faceLabel: `${c.member_count} member${c.member_count === 1 ? '' : 's'}`,
        cta: c.join_policy === 'open' ? 'Join' : 'View',
        onPress: () => navigation.navigate(ScreenName.ClubProfile, {clubId: c.id}),
      });
    });

    if (tiles.length === 0) {
      tiles.push({
        key: 'no_club',
        icon: dsIcons.chessEvents,
        tag: 'CLUBS',
        tagColor: '#67B3E1',
        title: "You're not in a club yet",
        sub: 'Join one to play club events, challenges and polls',
        faceLabel: 'Browse the club directory',
        cta: 'Find',
        onPress: goToClubs,
      });
    }
  }

  return (
    <View style={[styles.card, stacked && styles.cardStacked]}>
      <View style={styles.header}>
        <View style={styles.iconCircle}>
          <Image
            source={dsIcons.chessEvents}
            style={styles.headerIconImg}
            resizeMode="contain"
          />
        </View>
        <View style={{flex: 1, minWidth: 0}}>
          <CCText
            fontSize={15.5}
            fontWeight="bold"
            color={CCColors.TextColor.Dark}>
            Club activity
          </CCText>
          <CCText fontSize={12} color={CCColors.TextColor.Grey} showEllipses>
            {club
              ? CLUB_SUBTITLE_BY_PERSONA[persona]
              : 'Clubs on CircleChess'}
          </CCText>
        </View>
        <Pressable onPress={goToClubs}>
          <CCText fontSize={12} fontWeight="bold" color={CCColors.Green}>
            All clubs ›
          </CCText>
        </Pressable>
      </View>

      <View style={styles.list}>
        {tiles.slice(0, MAX_TILES).map(t => (
          <ClubTile key={t.key} tile={t} />
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: CCColors.White,
    borderRadius: radii.panel,
    padding: 18,
    // 0.3 against Review game's 0.7 — the same split every other row on the
    // page uses, so the seam between the columns stays one straight line.
    //
    // NOT flex-grow — see Dashboard styles.ts's note on reviewGameCard.
    // Measured live: this pair (886/395) and the goals/assessment pair
    // (877/403) drifted from the intended 896/384 by DIFFERENT amounts under
    // the identical 0.7/0.3 flex-grow declaration, and forcing flex-grow/
    // shrink/basis/min-width/max-width identical via !important DOM overrides
    // still left a residual gap — so it isn't fixable by tuning the flex
    // props. calc() sidesteps whatever that free-space-distribution quirk is.
    flex: 'none',
    width: 'calc(30% - 9.6px)',
    minWidth: 0,
    // Must match Dashboard styles.card (margin 8 / radii.panel / same shadow)
    // or this card's edges sit 8px off from Review game's beside it.
    margin: 8,
    shadowColor: CCColors.Primary.Dark,
    shadowRadius: 8,
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.05,
  },
  cardStacked: {
    // alignSelf, not width:'100%' — the card has margin 8 and would overflow.
    alignSelf: 'stretch',
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: 'auto',
    minWidth: 0,
    // Cancels card's fixed calc() width once the row goes vertical — see the
    // matching note on Dashboard styles.ts's stackedCol.
    width: 'auto',
  },
  header: {flexDirection: 'row', alignItems: 'center', gap: 11},
  iconCircle: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: '#E4F6EB',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  list: {flexDirection: 'column', gap: 9, marginTop: 14},
  tile: {
    borderWidth: 1,
    borderColor: CCColors.BorderLight,
    borderRadius: radii.chip,
    padding: 13,
    backgroundColor: CCColors.White,
    ...transition(180),
  },
  tileHovered: {
    ...hoverLift(3),
    borderColor: '#E7C55A',
    backgroundColor: '#FFFDF6',
  },
  tilePressed: pressSink(0.99),
  tileTopRow: {flexDirection: 'row', alignItems: 'center', gap: 10},
  tileIcon: {width: 22, height: 22, flexShrink: 0},
  headerIconImg: {width: 20, height: 20},
  tag: {letterSpacing: 0.5, flex: 1},
  tileBottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    marginTop: 10,
  },
  faceStack: {flexDirection: 'row'},
  face: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: CCColors.White,
    marginLeft: -5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ctaPill: {
    backgroundColor: '#FACF47',
    borderRadius: 32,
    paddingHorizontal: 12,
    paddingVertical: 5,
    flexShrink: 0,
  },
  progressTrack: {
    height: 4,
    borderRadius: 99,
    backgroundColor: CCColors.Primary.lightBrown,
    marginTop: 9,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 99,
    backgroundColor: CCColors.Green,
  },
});

export default ClubActivityCard;
